import { FunctionComponent, useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Modal } from "@/components/Modals/Modal";
import { TextArea } from "@/components/TextArea/TextArea";
import { ButtonColor } from "@/components/Button/types";

const SignalIgnoreFeedbackSchema = z.object({
	feedback: z.string().trim().min(3, "profileTracker.signalActions.ignoreFeedbackRequired"),
});

export type SignalIgnoreFeedbackFormValues = z.infer<typeof SignalIgnoreFeedbackSchema>;

interface SignalIgnoreFeedbackModalProps {
	isOpened: boolean;
	isSubmitting?: boolean;
	submitError?: string | null;
	onClose: () => void;
	onSubmit: (values: SignalIgnoreFeedbackFormValues) => Promise<void> | void;
}

export const SignalIgnoreFeedbackModal: FunctionComponent<SignalIgnoreFeedbackModalProps> = ({
	isOpened,
	isSubmitting = false,
	submitError,
	onClose,
	onSubmit,
}) => {
	const { t } = useTranslation();
	const {
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors, isValid },
	} = useForm<SignalIgnoreFeedbackFormValues>({
		defaultValues: { feedback: "" },
		mode: "onChange",
		resolver: zodResolver(SignalIgnoreFeedbackSchema),
	});

	useEffect(() => {
		if (!isOpened) {
			reset();
		}
	}, [isOpened, reset]);

	return (
		<Modal
			disableSave={!isValid || isSubmitting}
			handleClose={onClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isSubmitting}
			isOpened={isOpened}
			size="sm"
			submitButtonColor={ButtonColor.DANGER_SECONDARY}
			submitButtonText={t("profileTracker.signalActions.ignoreSubmit")}
			subtitle={t("profileTracker.signalActions.ignoreFeedbackDescription")}
			title={t("profileTracker.signalActions.ignoreFeedbackTitle")}
		>
			<div className="flex flex-col gap-2">
				<TextArea
					error={errors.feedback?.message ? t(errors.feedback.message) : undefined}
					handleChange={(event) =>
						setValue("feedback", event.target.value, { shouldDirty: true, shouldValidate: true })
					}
					label={t("profileTracker.signalActions.ignoreFeedbackLabel")}
					name="feedback"
					placeholder={t("profileTracker.signalActions.ignoreFeedbackPlaceholder")}
					showError={Boolean(errors.feedback)}
					value={watch("feedback")}
				/>
				{errors.feedback?.message && (
					<p className="text-xs text-status-error">{t(errors.feedback.message)}</p>
				)}
				{submitError && <p className="text-xs text-status-error">{submitError}</p>}
			</div>
		</Modal>
	);
};
